import React, {useCallback} from "react"
import cx from "classnames"
import Select from "react-select"
import CreatableSelect from "react-select/creatable"
import {get, isEqual} from "lodash"
import {useTranslation} from "react-i18next"
import {dropdownStyle} from "./dropdownStyle"

type SelectComponentsProps = {[key in string]: any}

export type KlassDropdownProps = SelectComponentsProps & {
    options?: any[]
    value?: any
    onChange?: any
    placeholder?: string
    valueKey?: string
    labelKey?: string
    hasEmptyOption?: boolean
    isDisabled?: boolean
    readOnly?: boolean
    isMulti?: boolean
    isGrouped?: boolean
    isClearable?: boolean
    stylesCustom?: any
    closeMenuOnSelect?: boolean
    error?: boolean
    showTop?: boolean
    creatable?: boolean
    isLoading?: boolean
    openMenuOnFocus?: boolean
    dropdownRef?: any
}

export function KlassDropdown(props: KlassDropdownProps) {
    const {
        stylesCustom,
        valueKey,
        labelKey,
        hasEmptyOption,
        options = [],
        isDisabled = false,
        readOnly = false,
        isClearable = false,
        isMulti,
        className,
        isGrouped,
        showTop = false,
        creatable = false,
        isLoading = false,
        openMenuOnFocus,
        dropdownRef,
        ...rest
    } = props

    const getMenuStyle = (provided) => ({
        ...provided,
        zIndex: 10,
        top: showTop ? "unset" : "100%",
        bottom: showTop ? "100%" : "unset"
    })

    const {t} = useTranslation("common")
    let valueKeySelect = valueKey || "id"
    let labelKeySelect = labelKey || "name"
    let styles = dropdownStyle
    styles.menu = getMenuStyle
    if (stylesCustom) {
        styles = {...styles, ...stylesCustom}
    }
    const customProps = {...rest, styles}
    const emptyValue = {[valueKeySelect]: "", [labelKeySelect]: t("selectField.emptyValue")}
    const extendedOptions = [...options]
    if (hasEmptyOption && options && options.length && !isEqual(options[0], emptyValue)) {
        extendedOptions.splice(0, 0, emptyValue)
    }

    const formatGroupLabel = (data) => (
        <div>
            <span>{data.label}</span>
        </div>
    )

    const getOptionValue = useCallback((option: any) => get(option, valueKeySelect), [valueKeySelect])
    const getOptionLabel = useCallback((option: any) => get(option, labelKeySelect), [labelKeySelect])
    const filterOption = useCallback(
        (candidate: {label: string; value: string; data: any}, input: string) => {
            const {data: option} = candidate
            if (input) {
                return `${get(option, valueKeySelect)} ${get(option, labelKeySelect)}`
                    .toLowerCase()
                    .includes(input.toLowerCase())
            }
            return true
        },
        [valueKeySelect, labelKeySelect]
    )

    const selectProps = {
        getOptionValue,
        getOptionLabel,
        filterOption: props.filterOption ?? filterOption,
        options: extendedOptions,
        menuIsOpen: readOnly ? false : undefined,
        isDisabled,
        isSearchable: !readOnly,
        isClearable: !!isClearable && !readOnly,
        closeMenuOnSelect: true,
        isMulti,
        isLoading,
        openMenuOnFocus,
        formatGroupLabel: isGrouped && formatGroupLabel,
        classNamePrefix: "klassapp-dropdown",
        className: cx("klassapp-dropdown", className, {readOnly})
    }

    const creatableSelectRef = React.useRef<CreatableSelect<any, boolean>>()

    if (creatable) {
        return (
            <CreatableSelect
                menuPortalTarget={document.body}
                {...selectProps}
                {...customProps}
                ref={creatableSelectRef}
            />
        )
    }
    return <Select menuPortalTarget={document.body} ref={dropdownRef} {...selectProps} {...customProps} />
}
